import React from 'react';
import { PRODUCT_MOCKUP, PRODUCT_MOCKUP_ALT } from '../data/product';

/** Flat speaker silhouette with a mic (2px-line identity). */
function Speaker({
  x,
  y,
  s = 1
}: {
  x: number;
  y: number;
  s?: number;
}) {
  return <g transform={`translate(${x} ${y}) scale(${s})`} data-id="element-263" data-mp="3sgm421xs2p7w">
      <circle cx="0" cy="-46" r="11" fill="#15213A" data-id="element-264" data-mp="bs3odbffu6c" />
      <path d="M-17 -30 Q0 -36 17 -30 L13 12 L-13 12 Z" fill="#15213A" data-id="element-265" data-mp="ca7pw4bzcot" />
      <path d="M13 -26 L28 -40" stroke="#15213A" strokeWidth="5" strokeLinecap="round" data-id="element-266" data-mp="o12wn8mjck9x" />
      <rect x="26" y="-50" width="8" height="13" rx="4" fill="#F4B23E" transform="rotate(40 30 -44)" data-id="element-267" data-mp="t0yyb17rfzmb" />
      <path d="M-8 12 L-10 44 M8 12 L10 44" stroke="#15213A" strokeWidth="6" strokeLinecap="round" data-id="element-268" data-mp="f9kdcoualocm" />
    </g>;
}

/** Soundwave bars. */
function Wave({
  x,
  y,
  w
}: {
  x: number;
  y: number;
  w: number;
}) {
  const bars = [6, 14, 22, 12, 28, 18, 9, 24, 15, 7, 19, 11, 26, 13, 6];
  const step = w / bars.length;
  return <g transform={`translate(${x} ${y})`} data-id="element-269" data-mp="1bryscgu43zmg">
      {bars.map((h, i) => <rect key={i} x={i * step} y={-h / 2} width={step * 0.45} height={h} rx={step * 0.22} fill={i % 5 === 2 ? '#E8503A' : i % 3 === 0 ? '#1F8A8C' : '#F4B23E'} data-id="element-270" data-mp="24btasldl0jt7" data-mpi={i} />)}
    </g>;
}

/**
 * Shows the uploaded mockup when PRODUCT_MOCKUP is set; otherwise draws the
 * cover in code: paper page, spotlight on a speaker, soundwave, audience tags.
 */
export function ProductVisual({
  className = '',
  lazy = false
}: {
  className?: string;
  lazy?: boolean;
}) {
  if (PRODUCT_MOCKUP) {
    return <img src={PRODUCT_MOCKUP} alt={PRODUCT_MOCKUP_ALT} loading={lazy ? 'lazy' : undefined} className={`mx-auto aspect-square h-auto w-full rounded-2xl object-cover shadow-book ${className}`} data-id="element-271" data-mp="2j3licb74wtkd" />;
  }
  return <div className={`relative mx-auto ${className}`} role="img" aria-label={PRODUCT_MOCKUP_ALT} data-id="element-272" data-mp="12ns9tfe489mh">
      {/* the other volumes, fanned behind */}
      <div className="absolute inset-0 translate-x-[9%] translate-y-[2%] rotate-[6deg] rounded-[14px] bg-teal shadow-book" aria-hidden="true" data-id="element-273" data-mp="xq8wqit5m2w4" />
      <div className="absolute inset-0 translate-x-[6%] translate-y-[1.5%] rotate-[4deg] rounded-[14px] bg-coral shadow-book" aria-hidden="true" data-id="element-274" data-mp="32q9yn0gqhrj9" />
      <div className="absolute inset-0 translate-x-[3%] translate-y-[1%] rotate-[2deg] rounded-[14px] bg-amber shadow-book" aria-hidden="true" data-id="element-275" data-mp="36t6lx4h7ybtj" />

      <div style={{
      containerType: 'inline-size'
    }} className="relative aspect-[4/3] overflow-hidden rounded-[14px] bg-cloud shadow-book" data-id="element-276" data-mp="3gpt4fmm2oqse">
        <span className="wave-strip absolute inset-x-0 top-0 h-[3.2%]" aria-hidden="true" data-id="element-277" data-mp="3qet6jevvg2at" />

        {/* spotlight + stage */}
        <svg viewBox="0 0 400 300" className="absolute inset-0 h-full w-full" aria-hidden="true" data-id="element-278" data-mp="3ugy06dmbkyxg">
          <defs data-id="element-279" data-mp="3an0akt1q47p">
            <linearGradient id="beam" x1="0" y1="0" x2="0" y2="1" data-id="element-280" data-mp="3b6crlzxg0ec">
              <stop offset="0" stopColor="#F4B23E" stopOpacity="0.55" data-id="element-281" data-mp="88yawxr60iqz" />
              <stop offset="1" stopColor="#F4B23E" stopOpacity="0.08" data-id="element-282" data-mp="86vcpqfuzjb6" />
            </linearGradient>
          </defs>
          <path d="M318 0 L370 0 L392 236 L250 236 Z" fill="url(#beam)" data-id="element-283" data-mp="de1ukfrvvgfj" />
          <ellipse cx="321" cy="238" rx="72" ry="9" fill="#F4B23E" opacity="0.45" data-id="element-284" data-mp="dg4srn36wfvc" />
          <Speaker x={318} y={192} s={1.05} data-id="element-285" data-mp="8fh1r1eynz14" />
          <Wave x={28} y={262} w={190} data-id="element-286" data-mp="4dn7pbz9tt9x" />
        </svg>

        <div className="relative px-[7%] pt-[8%]" data-id="element-287" data-mp="4f6n70d3s0an">
          <p className="font-display text-[3cqw] font-bold uppercase tracking-[0.2em] text-coral-deep" data-id="element-288" data-mp="1145x0jtc5a4y">
            A full year · 6 volumes
          </p>
          <p className="mt-[2cqw] font-display text-[9cqw] font-extrabold leading-[0.95] tracking-tight text-ink" data-id="element-289" data-mp="w5bd1ejmtfj9">
            52 Weeks
            <br data-id="element-290" data-mp="nchjcc3fudlv" />
            of Speaking
          </p>
          <p className="mt-[2.4cqw] max-w-[56%] font-sans text-[3.1cqw] font-medium leading-snug text-slate" data-id="element-291" data-mp="nbzh8yu3h561">
            Ready-to-run public speaking sessions for students, clubs &amp; adults
          </p>
          <div className="mt-[3cqw] flex gap-[1.4cqw]" data-id="element-292" data-mp="1f1ays7m0nmue">
            {[['Students', 'bg-amber text-ink'], ['Club', 'bg-teal text-white'], ['Adults', 'bg-coral text-white']].map(([t, c]) => <span key={t} className={`rounded-full px-[1.8cqw] py-[0.7cqw] font-display text-[2.3cqw] font-bold uppercase tracking-wider ${c}`} data-id="element-293" data-mp="qlhi80017vns">
                {t}
              </span>)}
          </div>
        </div>
      </div>
    </div>;
}